import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Input, Row, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useState } from "react";
import { EEnvatoPurchaseStatus } from "@devowl-wp/api-real-commerce";
import { IMPREZA_BUTTON_PROPS } from "../../../../components/provider.js";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { useStores } from "../../../../store/stores.js";
import { __, _i } from "../../../../utils/i18n.js";
import { getLegalManufacturerUrls } from "../../../../utils/legalEntities.js";
import { useAccountV2ErrorHandling } from "../../hooks/useAccountV2ErrorHandling.js";
const INITIAL_VALUES = {
    purchaseCode: "",
    firstName: "",
    lastName: "",
    email: "",
    terms: false
};
const EnvatoOnboardingForm = observer(()=>{
    const { accountV2Store } = useStores();
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [isBusy, setIsBusy] = useState(false);
    const [successStatus, setSuccessStatus] = useState();
    const handleError = useAccountV2ErrorHandling();
    const { terms: termsUrl, privacy: privacyUrl } = getLegalManufacturerUrls();
    const onFinish = async (values)=>{
        setIsBusy(true);
        try {
            const { envatoPurchase: { status } } = await accountV2Store.createAccountEnvato(values);
            setSuccessStatus(status);
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleError(e);
            }
        } finally{
            setIsBusy(false);
        }
    };
    if (successStatus !== undefined) {
        const isComplete = successStatus === EEnvatoPurchaseStatus.Complete;
        return /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-auth-card",
                style: {
                    background: token.colorBgContainer,
                    borderColor: token.colorBorder
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "rc-account-v2-auth-card__icon rc-account-v2-auth-card__icon--lg",
                        style: {
                            color: token.colorSuccess
                        },
                        children: /*#__PURE__*/ _jsx(CheckCircleFilled, {})
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "rc-account-v2-auth-card__title",
                        style: {
                            color: token.colorTextHeading
                        },
                        children: isComplete ? __("We've added your license to your devowl.io account.") : __("Check your inbox to activate the account.")
                    }),
                    isComplete && /*#__PURE__*/ _jsx("div", {
                        className: "rc-account-v2-auth-card__cross-links rc-account-v2-auth-card__cross-links--centered rc-account-v2-auth-card__cross-links--spaced",
                        children: /*#__PURE__*/ _jsx("a", {
                            href: "#/",
                            children: __("Sign in")
                        })
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card-wrap rc-account-v2-auth-card-wrap--wide",
                children: /*#__PURE__*/ _jsx(Spin, {
                    spinning: isBusy,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-auth-card rc-account-v2-auth-card--left",
                        style: {
                            background: token.colorBgContainer,
                            borderColor: token.colorBorder
                        },
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "rc-account-v2-auth-card__title",
                                style: {
                                    color: token.colorTextHeading
                                },
                                children: __("Activate your codecanyon.net purchase")
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "rc-account-v2-auth-card__subtitle",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: __("We sell some products on codecanyon.net. Add your purchase here to manage your licenses and activations.")
                            }),
                            /*#__PURE__*/ _jsxs(Form, {
                                form: form,
                                layout: "vertical",
                                initialValues: INITIAL_VALUES,
                                onFinish: onFinish,
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "purchaseCode",
                                        label: __("Purchase code (license key)"),
                                        rules: [
                                            {
                                                required: true,
                                                message: __("Please enter your purchase code.")
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gutter: 16,
                                        children: [
                                            /*#__PURE__*/ _jsx(Col, {
                                                xs: 24,
                                                md: 12,
                                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                                    name: "firstName",
                                                    label: __("First name"),
                                                    rules: [
                                                        {
                                                            required: true,
                                                            message: __("Please enter your first name.")
                                                        }
                                                    ],
                                                    children: /*#__PURE__*/ _jsx(Input, {})
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Col, {
                                                xs: 24,
                                                md: 12,
                                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                                    name: "lastName",
                                                    label: __("Last name"),
                                                    rules: [
                                                        {
                                                            required: true,
                                                            message: __("Please enter your last name.")
                                                        }
                                                    ],
                                                    children: /*#__PURE__*/ _jsx(Input, {})
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "email",
                                        label: __("Email address"),
                                        rules: [
                                            {
                                                type: "email",
                                                required: true,
                                                message: __("Please enter a valid email address.")
                                            }
                                        ],
                                        extra: __("May differ from your codecanyon.net email."),
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            type: "email"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "terms",
                                        valuePropName: "checked",
                                        className: "rc-account-v2-auth-card__terms-item",
                                        rules: [
                                            {
                                                type: "boolean",
                                                required: true,
                                                transform: (v)=>v || undefined,
                                                message: __("Please confirm that you have read the Terms and Conditions.")
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            className: "rc-account-v2-auth-card__checkbox",
                                            style: {
                                                color: token.colorText
                                            },
                                            children: _i(__("I have read and agree to the {{a}}Terms and Conditions{{/a}}."), {
                                                a: /*#__PURE__*/ _jsx("a", {
                                                    href: termsUrl,
                                                    target: "_blank",
                                                    rel: "noreferrer"
                                                })
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "rc-account-v2-auth-card__info-note",
                                        style: {
                                            color: token.colorText
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                                                "aria-hidden": true,
                                                className: "rc-account-v2-auth-card__info-note-icon",
                                                style: {
                                                    color: token.colorText
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: _i(__("Information on the processing of your personal data can be found in our {{a}}privacy policy{{/a}}."), {
                                                    a: /*#__PURE__*/ _jsx("a", {
                                                        href: privacyUrl,
                                                        target: "_blank",
                                                        rel: "noopener noreferrer"
                                                    })
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        className: "rc-account-v2-auth-card__cta",
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            type: "primary",
                                            size: "large",
                                            htmlType: "submit",
                                            block: true,
                                            ...IMPREZA_BUTTON_PROPS,
                                            children: __("Add to your devowl.io account")
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-auth-card__cross-links rc-account-v2-auth-card__cross-links--centered rc-account-v2-auth-card__cross-links--footer",
                children: /*#__PURE__*/ _jsxs("span", {
                    style: {
                        color: token.colorTextSecondary
                    },
                    children: [
                        __("Already have a devowl.io account?"),
                        " ",
                        /*#__PURE__*/ _jsx("a", {
                            href: "#/",
                            style: {
                                color: token.colorPrimary
                            },
                            children: __("Sign in instead")
                        })
                    ]
                })
            })
        ]
    });
});
export { EnvatoOnboardingForm };
